// EventRisksTab — list of risks linked to the current event, with link/unlink.
// Data comes from GET /events/{event_id}/risks.
// Event owns this relationship via event_risk_links table.

import { useState, useMemo } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { eventsApi, type LinkedEventRisk } from '@/lib/api/events'
import { risksApi, type Risk, lifecycleStateLabels, lifecycleStateColors } from '@/lib/api/risks'
import { registersApi } from '@/lib/api/registers'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { LinkPickerDialog, type PickerTreeNode } from '@/components/shared/LinkPickerDialog'
import { ConfirmUnlinkDialog } from '@/components/shared/ConfirmUnlinkDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertTriangle,
  FolderOpen,
  Calendar,
  ExternalLink,
  Plus,
  Unlink,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export function EventRisksTab() {
  const { slug } = useTenant()
  const { eventId } = useParams({ strict: false }) as { eventId: string }
  const navigate = useNavigate()
  const api = useApi()
  const queryClient = useQueryClient()
  const evtApi = eventsApi(api)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedEventRisk | null>(null)

  // Linked risks for this event
  const {
    data: risks,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.events.risks(slug, eventId),
    queryFn: () => evtApi.risks(eventId),
  })

  // Register tree for the picker
  const { data: registerTree, isLoading: registersLoading } = useQuery({
    queryKey: queryKeys.registers.tree(slug),
    queryFn: () => registersApi(api).tree(),
    enabled: pickerOpen,
  })

  // All risks across accessible registers (fetched per register)
  const { data: allRisks, isLoading: risksLoading } = useQuery({
    queryKey: [...queryKeys.registers.tree(slug), 'all-risks-for-picker'],
    queryFn: async () => {
      const tree = await registersApi(api).tree()
      const rApi = risksApi(api)
      const results = await Promise.all(
        (tree ?? []).map((reg) => rApi.list(reg.id).catch(() => [] as Risk[])),
      )
      return results.flat()
    },
    enabled: pickerOpen,
  })

  const existingIds = useMemo(
    () => new Set(risks?.map((r) => r.risk_id) ?? []),
    [risks],
  )

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: ({ linkId, notes }: { linkId: string; notes: string }) =>
      evtApi.unlinkRisk(eventId, linkId, notes || undefined),
    onSuccess: () => {
      setUnlinkTarget(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.events.risks(slug, eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(slug, eventId) })
    },
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load linked risks"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {risks?.length ?? 0} linked risk{risks?.length !== 1 ? 's' : ''}
        </div>
        <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Link Risk
        </Button>
      </div>

      {/* Empty state */}
      {(!risks || risks.length === 0) && (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm">No risks are linked to this event.</p>
            <p className="text-xs text-muted-foreground/60">
              Use the button above to link risks to this event.
            </p>
          </div>
        </div>
      )}

      {/* Risk list */}
      {risks && risks.length > 0 && (
        <div className="divide-y rounded-lg border">
          {risks.map((risk) => (
            <RiskRow
              key={risk.link_id}
              risk={risk}
              onClick={() => navigate({ to: `/t/${slug}/risks/${risk.risk_id}` })}
              onUnlink={() => setUnlinkTarget(risk)}
            />
          ))}
        </div>
      )}

      {/* Link picker dialog */}
      <LinkPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onLinked={() => {
          setPickerOpen(false)
          queryClient.invalidateQueries({ queryKey: queryKeys.events.risks(slug, eventId) })
          queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(slug, eventId) })
        }}
        title="Link Risk"
        description="Select a risk to link to this event."
        treeNodes={registerTree as PickerTreeNode[] | undefined}
        objects={allRisks}
        isLoading={risksLoading || registersLoading}
        existingIds={existingIds}
        getObjectId={(r: Risk) => r.id}
        getObjectParentId={(r: Risk) => r.register_id}
        getObjectSearchText={(r: Risk) => r.name}
        renderObject={(r: Risk, isSelected: boolean) => (
          <RiskPickerRow risk={r} isSelected={isSelected} />
        )}
        linkFn={(riskId: string) => evtApi.linkRisk(eventId, riskId)}
        linkButtonLabel="Link Risk"
        searchPlaceholder="Search risks or registers..."
      />

      {/* Unlink confirmation */}
      {unlinkTarget && (
        <ConfirmUnlinkDialog
          open={!!unlinkTarget}
          onClose={() => setUnlinkTarget(null)}
          onConfirm={(notes) =>
            unlinkMutation.mutate({ linkId: unlinkTarget.link_id, notes })
          }
          title="Remove Risk Link"
          description={`Remove the link between this event and "${unlinkTarget.risk_name}"?`}
          isPending={unlinkMutation.isPending}
        />
      )}
    </div>
  )
}

function RiskRow({
  risk,
  onClick,
  onUnlink,
}: {
  risk: LinkedEventRisk
  onClick: () => void
  onUnlink: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{risk.risk_name}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
      </div>

      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <FolderOpen className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{risk.register_name}</span>
      </div>

      <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
        <Calendar className="h-3 w-3" />
        <span>{new Date(risk.linked_at).toLocaleDateString()}</span>
      </div>

      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          lifecycleStateColors[risk.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {lifecycleStateLabels[risk.lifecycle_state] ?? risk.lifecycle_state}
      </Badge>

      <button
        className="flex-shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation()
          onUnlink()
        }}
        title="Remove link"
      >
        <Unlink className="h-4 w-4" />
      </button>
    </div>
  )
}

function RiskPickerRow({
  risk,
  isSelected,
}: {
  risk: Risk
  isSelected: boolean
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={cn('truncate', isSelected && 'font-medium')}>{risk.name}</span>
      <Badge
        variant="outline"
        className={cn(
          'ml-auto flex-shrink-0 px-1.5 py-0 text-xs',
          lifecycleStateColors[risk.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {lifecycleStateLabels[risk.lifecycle_state] ?? risk.lifecycle_state}
      </Badge>
    </div>
  )
}
